import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Chip, CircularProgress, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import TimeToLeaveIcon from '@mui/icons-material/TimeToLeave';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { useNavigate } from 'react-router-dom';
import { AxiosInstanceService } from '../../../services/axiosInstance';
import { apiRoute } from '../../../util/constant';
import DashboardMetricCard from './DashboardMetricCard';

const getVehicleTitle = (vehicle) => {
  const make = vehicle?.make?.name || vehicle?.make || '';
  const model = vehicle?.model?.name || vehicle?.model || '';
  return [vehicle?.year, make, model].filter(Boolean).join(' ') || '-';
};

const getCountTotal = (items = []) => {
  const totalItem = items.find((item) => item?.name?.toString().toUpperCase() === 'TOTAL');
  if (totalItem) return Number(totalItem.count) || 0;

  return items.reduce((sum, item) => sum + (Number(item?.count) || 0), 0);
};

const getCountBreakdown = (items = []) =>
  items.filter((item) => item?.name?.toString().toUpperCase() !== 'TOTAL');

const ClientDashboardHome = () => {
  const navigate = useNavigate();
  const [vehiclesData, setVehiclesData] = useState({ content: [], totalElements: 0 });
  const [counts, setCounts] = useState({ vehicle: [], container: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let mounted = true;

    const loadData = async () => {
      try {
        setLoading(true);
        const instance = await AxiosInstanceService.getAxiosInstance();
        const [vehiclesResponse, vehicleCountResponse, containerCountResponse] = await Promise.all([
          instance.get(`${apiRoute.getFetchData}?PageNumber=0&PageSize=5&SortBy=entryDate&SortOrder=desc`),
          instance.get(`${apiRoute.getManage}vehicle/count`),
          instance.get(`${apiRoute.getManage}container/count`),
        ]);

        if (mounted) {
          setVehiclesData(vehiclesResponse.data || { content: [], totalElements: 0 });
          setCounts({
            vehicle: Array.isArray(vehicleCountResponse.data) ? vehicleCountResponse.data : [],
            container: Array.isArray(containerCountResponse.data) ? containerCountResponse.data : [],
          });
          setError('');
        }
      } catch (err) {
        if (mounted) setError(err?.response?.data?.message || err.message || 'Unable to load dashboard');
      } finally {
        if (mounted) setLoading(false);
      }
    };

    loadData();
    return () => {
      mounted = false;
    };
  }, []);

  const vehicleTotal = useMemo(
    () => getCountTotal(counts.vehicle),
    [counts.vehicle]
  );

  const containerTotal = useMemo(
    () => getCountTotal(counts.container),
    [counts.container]
  );

  const statusChips = useMemo(() => {
    return getCountBreakdown(counts.vehicle).map((item) => ({ label: item.name || item.type || 'Count', count: item.count || 0 }));
  }, [counts.vehicle]);

  const containerChips = useMemo(() => {
    return getCountBreakdown(counts.container).map((item) => ({ label: item.name || item.type || 'Count', count: item.count || 0 }));
  }, [counts.container]);

  if (loading) {
    return (
      <Box sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Typography sx={{ color: '#b42318', mb: 2, fontWeight: 700 }}>
          {error}
        </Typography>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
          gap: 2,
        }}
      >
        <DashboardMetricCard
          icon={<TimeToLeaveIcon />}
          title="My Vehicles"
          value={vehicleTotal}
          subtitle="Vehicles available in your portal"
          chips={statusChips}
          onClick={() => navigate('/vehicles')}
        />
        <DashboardMetricCard
          icon={<Inventory2Icon />}
          title="Containers"
          value={containerTotal}
          subtitle="Containers currently visible to your account"
          chips={containerChips}
          onClick={() => navigate('/containers')}
        />
      </Box>

      <Box
        sx={{
          mt: 3,
          p: 2,
          borderRadius: 2,
          bgcolor: '#fff',
          border: '1px solid #e6ebef',
          boxShadow: '0 6px 18px rgba(11, 60, 93, 0.08)',
        }}
      >
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }} sx={{ mb: 2 }}>
          <Box>
            <Typography sx={{ color: '#0B3C5D', fontWeight: 800, fontSize: 20 }}>
              Recent Vehicles
            </Typography>
            <Typography sx={{ color: '#667785', mt: 0.5 }}>
              Latest vehicles from your account.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
            <Button startIcon={<AddCircleOutlineIcon />} variant="contained" onClick={() => navigate('/VehicleaddEdit', { state: { inTransit: true } })}>
              Add Vehicle
            </Button>
            <Button variant="outlined" onClick={() => navigate('/vehicles')}>View Vehicles</Button>
            <Button variant="outlined" onClick={() => navigate('/containers')}>View Containers</Button>
          </Stack>
        </Stack>

        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#0B3C5D' }}>
              <TableCell sx={{ color: '#D9B310' }}>ID</TableCell>
              <TableCell sx={{ color: '#D9B310' }}>Vehicle</TableCell>
              <TableCell sx={{ color: '#D9B310' }}>VIN</TableCell>
              <TableCell sx={{ color: '#D9B310' }}>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(vehiclesData.content || []).map((vehicle) => (
              <TableRow key={vehicle?.id} hover>
                <TableCell>{vehicle?.id || '-'}</TableCell>
                <TableCell>{getVehicleTitle(vehicle)}</TableCell>
                <TableCell>{vehicle?.vin || '-'}</TableCell>
                <TableCell>
                  <Chip size="small" label={vehicle?.status?.name || vehicle?.status || '-'} variant="outlined" />
                </TableCell>
              </TableRow>
            ))}
            {(!vehiclesData.content || vehiclesData.content.length === 0) && (
              <TableRow>
                <TableCell colSpan={4}>
                  <Typography sx={{ color: '#667785' }}>No vehicles</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Box>
    </Box>
  );
};

export default ClientDashboardHome;
